import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Oval as Loader } from 'react-loader-spinner';

import '../../message.css';
import LazyImage from '../../../../../components/LazyImage/LazyImage';
import { MatrixContext } from '../../../../../MatrixProvider';
import { useNavigation } from '../../../../../shared/utils/useNavigation';
import { formatUsdAmountDisplay, getUserRole } from '../../../../../shared/utils/function';
import { ROLE } from '../../../../../shared/constants/common';
import { fetchMatrixAllRooms } from '../../../../../shared/actions/matrixAction';
import { useMatrixRooms } from '../../../../../shared/utils/matrix/useMatrixRooms';
import { DEFAULT_AVATAR } from '../../../../../shared/utils/matrix/matrixRoomUtils';
import {
  safeString,
  getMatrixRoomsArray,
  getPendingTipsForUserType,
  CenteredLoader,
  isMongoObjectIdString,
} from '../../../../../shared/utils/chatUtil';
import ModernHeader from '../../../../../components/layout/Headers/ModernHeader';
import { ROUTES } from '../../../../../shared/constants/routes';
import {
  MESSAGE_TIP_STATUS,
  MESSAGE_REPLY_DEADLINE_DAYS_EXTENDED,
  WHALE_THRESHOLD,
} from '../../../../../shared/constants/message';
import { computeRoomIsPriority, getFirstTipDeadlineInfo } from '../../../../../shared/utils/matrix/matrixReadUtils';


const UNKNOWN_USER_LABEL = 'Unknown';
const LIST_RENDER_INITIAL = 40;
const LIST_RENDER_STEP = 40;

const NewMessagePage = () => {
  const dispatch = useDispatch();
  const {
    allRooms: matrixRooms = {},
    allRoomsLoading: matrixLoading = false,
    allRoomsLoadingMore: matrixLoadingMore = false,
    allRoomsPagination = {},
    hasAllRoomsLoaded: matrixHasRoomsLoaded = false,
    allRoomsError: matrixError = null,
  } = useSelector((state) => state.matrix || {});

  const { client, isInitialized, matrixUserInfo } = useContext(MatrixContext);

  const navigate = useNavigation();
  const [searchQuery, setSearchQuery] = useState('');
  const [roomMetadataReady, setRoomMetadataReady] = useState(false);
  const [visibleListCount, setVisibleListCount] = useState(LIST_RENDER_INITIAL);

  const initialFetchDoneRef = useRef(false);
  const listLoadMoreSentinelRef = useRef(null);

  const userRole = getUserRole();
  const isCreator =
    userRole === ROLE.CREATOR_SUB || userRole === ROLE.CREATOR_CONTENT;

  const getRoomId = useCallback(
    (room) => room?.roomId || room?._id || room?.id || null,
    [],
  );

  const { rooms: roomsList } = useMatrixRooms({
    client,
    isInitialized,
    matrixUserInfo,
    options: {
      loadingTimeout: 5000,
      fallbackTimeout: 3000,
    },
  });

  const mapAllRoomToDisplayRoom = useCallback((matrixRoomData, clientRoom = {}) => {
    const otherUser = matrixRoomData?.otherUser;
    const isFirstMessageTip = Boolean(otherUser?.isFirstMessageTip);
    const isCreatorReplyToTip = Boolean(otherUser?.isCreatorReplyToTip);
    const firstTipCreatedAt = otherUser?.firstTipCreatedAt || null;
    const firstTipIsExtended = Boolean(otherUser?.firstTipIsExtended);
    const isPriority = computeRoomIsPriority(otherUser);
    const tipStatus = otherUser?.firstTipState;
    const tipId = otherUser?.firstTipId || null;

    let daysRemaining = null;
    let daysLeftToReply = null;
    let extended = null;
    if (
      firstTipCreatedAt &&
      isFirstMessageTip &&
      !isCreatorReplyToTip &&
      tipStatus === MESSAGE_TIP_STATUS.PENDING_CREATOR_REPLY
    ) {
      const deadlineInfo = getFirstTipDeadlineInfo(firstTipCreatedAt, firstTipIsExtended);
      if (deadlineInfo) {
        daysRemaining = deadlineInfo.label;
        daysLeftToReply = deadlineInfo.daysLeft;
        if (firstTipIsExtended) {
          extended = `Extended +${MESSAGE_REPLY_DEADLINE_DAYS_EXTENDED} days`;
        }
      }
    }

    const pendingTips = getPendingTipsForUserType(
      isCreator,
      otherUser,
      clientRoom?.lastMessage || clientRoom?.last_message || clientRoom?.message || '',
    );

    return {
      ...clientRoom,
      roomId: matrixRoomData.roomid,
      userId: otherUser?._id || clientRoom.userId || null,
      username: otherUser?.username || clientRoom.username || UNKNOWN_USER_LABEL,
      profilePicture: otherUser?.profile_picture || clientRoom.profilePicture || DEFAULT_AVATAR,
      subscribed: otherUser?.subscribed || false,
      total: otherUser?.total ?? 0,
      totalSixtyDays: otherUser?.totalSixtyDays ?? 0,
      isFirstMessageTip,
      isCreatorReplyToTip,
      isPriority,
      daysRemaining,
      daysLeftToReply,
      extended,
      tipId,
      matrixEventId: otherUser?.firstTipMatrixEventId || null,
      tipStatus,
      isExtended: firstTipIsExtended,
      pendingTips,
    };
  }, [isCreator]);

  // `/matrix/all-rooms` is the subscriber list for this page; Matrix client rooms only enrich timestamps.
  const enrichedRoomsList = useMemo(() => {
    const matrixRoomsArray = getMatrixRoomsArray(matrixRooms);

    if (!isInitialized || !roomMetadataReady) {
      return [];
    }

    const clientRoomsMap = new Map(
      (roomsList || [])
        .map((room) => {
          const roomId = getRoomId(room);
          return roomId ? [roomId, room] : null;
        })
        .filter(Boolean),
    );

    return matrixRoomsArray
      .filter((matrixRoom) => matrixRoom?.roomid && matrixRoom?.otherUser)
      .map((matrixRoomData) => {
        const clientRoom = clientRoomsMap.get(matrixRoomData.roomid) || {};
        return mapAllRoomToDisplayRoom(matrixRoomData, clientRoom);
      });
  }, [matrixRooms, roomMetadataReady, roomsList, isInitialized, getRoomId, mapAllRoomToDisplayRoom]);

  const filteredRoomsList = useMemo(() => {
    const q = safeString(searchQuery).trim().toLowerCase();
    // Backend `/matrix/all-rooms` already returns the subscriber list (including users with no messages yet).
    const baseRooms = enrichedRoomsList;

    if (!q) return baseRooms;

    return baseRooms.filter((room) => {
      const name = safeString(room?.username).toLowerCase();
      const lastMessage = safeString(room?.lastMessage || room?.last_message || room?.message || '').toLowerCase();
      return name.includes(q) || lastMessage.includes(q);
    });
  }, [enrichedRoomsList, searchQuery]);

  const visibleRoomsList = useMemo(
    () => filteredRoomsList.slice(0, visibleListCount),
    [filteredRoomsList, visibleListCount],
  );

  const hasMoreRoomsToRender = visibleListCount < filteredRoomsList.length;
  const hasMoreBackendRooms = Boolean(allRoomsPagination?.hasMore);
  const hasMoreToShow = hasMoreRoomsToRender || hasMoreBackendRooms;

  useEffect(() => {
    setVisibleListCount(LIST_RENDER_INITIAL);
  }, [searchQuery]);

  useEffect(() => {
    const sentinel = listLoadMoreSentinelRef.current;
    if (!sentinel || !hasMoreToShow) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;

        setVisibleListCount((prev) => {
          if (prev >= filteredRoomsList.length) return prev;
          return Math.min(prev + LIST_RENDER_STEP, filteredRoomsList.length);
        });

        if (hasMoreBackendRooms && !matrixLoadingMore && !matrixLoading) {
          dispatch(fetchMatrixAllRooms({ append: true }));
        }
      },
      { rootMargin: '240px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    hasMoreToShow,
    hasMoreBackendRooms,
    filteredRoomsList.length,
    matrixLoadingMore,
    matrixLoading,
    dispatch,
  ]);

  useEffect(() => {
    if (!isInitialized || initialFetchDoneRef.current) {
      return;
    }
    initialFetchDoneRef.current = true;
    if (!matrixHasRoomsLoaded) {
      dispatch(fetchMatrixAllRooms());
    }
  }, [dispatch, isInitialized, matrixHasRoomsLoaded]);

  useEffect(() => {
    if (!isInitialized) {
      setRoomMetadataReady(false);
      return;
    }
    if (matrixLoading) return;

    if (matrixHasRoomsLoaded) {
      setRoomMetadataReady(true);
      return;
    }
    if (matrixError) {
      setRoomMetadataReady(false);
    }
  }, [isInitialized, matrixLoading, matrixHasRoomsLoaded, matrixError]);

  const hasSubscribers = filteredRoomsList.length > 0;

  // Keep showing the loader until `/matrix/all-rooms` metadata is ready,
  // so we don't briefly flash "No subscribers" while the list is still loading.
  const isLoadingSubscribers =
    !isInitialized ||
    (matrixLoading && !roomMetadataReady);

  const shouldShowEmptySubscribersState =
    !isLoadingSubscribers &&
    !matrixError &&
    roomMetadataReady &&
    !hasSubscribers;

  const roomsWithConversations = useMemo(
    () => enrichedRoomsList.filter((room) => room?.lastTimestamp > 0),
    [enrichedRoomsList],
  );

  const handleSubscriberUsernameClick = useCallback(
    (item) => {
      if (!item) return;

      const appUserId =
        item.userId != null && isMongoObjectIdString(String(item.userId))
          ? String(item.userId)
          : null;
      if (!appUserId) return;

      const roomId = getRoomId(item);
      const hasConversation = roomsWithConversations.some((room) => getRoomId(room) === roomId);

      if (hasConversation) {
        // Pass navigation state so the chat page knows we came from New Message
        navigate.toChat(appUserId, { state: { fromNewMessage: true } });
      } else {
        // No prior conversation: take user to the new chat intro screen
        navigate.toNewChat(appUserId, {
          state: {
            fromNewMessage: true,
            username: item.username,
            profilePicture: item.profilePicture,
          },
        });
      }
    },
    [roomsWithConversations, navigate, getRoomId],
  );

  const isPendingAmountEnabled = (item) => item?.isFirstMessageTip
    && !item?.isCreatorReplyToTip
    && !item?.isPriority
    && item?.daysLeftToReply > 0
    && item?.pendingTips > 0;

  return (
    <div className="modern-message-page">
      <ModernHeader
        customTitle="New Message"
        showButtonBorder={false}
        hideSearchIcon
        showAvatar={false}
        showBackButton
        backButtonUrl={ROUTES.MY_MESSAGES}
      />

      <main className="modern-contact-main">
        <div className="cm_user_chat_search">
          <input
            type="text"
            placeholder="Search Recipient..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <h3 className="cm_sub_title">Your Subscribers</h3>
        <div className="cm_sub_list">
          {isLoadingSubscribers && !hasSubscribers && (
            <CenteredLoader size={32} text="Loading subscribers..." />
          )}

          {!isLoadingSubscribers && matrixError && !hasSubscribers && (
            <div className="text-center p-3">Failed to load subscribers</div>
          )}

          {shouldShowEmptySubscribersState && (
            <div className="text-center p-3">No subscribers found</div>
          )}

          {visibleRoomsList.map((item) => {
            const spendClassName = item.isPriority
              ? 'subscriber_total_spend_priority'
              : 'subscriber_total_spend';
            const isWhale = Number(item.total) >= WHALE_THRESHOLD && isCreator;

            return (
              <div
                key={item.roomId || item._id || item.id}
                className="cm_sub_item"
              >
                <div className="cm_sub_item_avatar">
                  <LazyImage
                    src={item.profilePicture}
                    alt={`@${item.username}`}
                    onError={(e) => {
                      // preserve existing fallback behavior
                      if (e?.target) {
                        e.target.onerror = null;
                        e.target.src = DEFAULT_AVATAR;
                      }
                    }}
                  />
                </div>
                <div className="cm_sub_item_username">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleSubscriberUsernameClick(item)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSubscriberUsernameClick(item);
                      }
                    }}
                  >
                    @{item.username}

                    {isPendingAmountEnabled(item) && (
                       <span className='amount_pending'>
                          +${formatUsdAmountDisplay(item?.pendingTips)} pending
                        </span>
                      )}

                    {item.isPriority && (
                      <span className="msg_card_priority_badge">
                        <b className="dot_green_msg" aria-hidden="true" />
                        <span>Priority</span>
                      </span>
                    )}

                    {isWhale && <span aria-label="Whale subscriber"> 🐋</span>}
                  </div>
                  
                  {!isPendingAmountEnabled(item) && item.total > 0 && (
                    <div>
                      <span className={spendClassName}>
                        $
                        {formatUsdAmountDisplay(item.total)}
                        {' '}
                        spent
                      </span>
                    </div>
                  )}

                  {item.daysRemaining && (
                    <div><span className='cm_day_remaining'>{item.daysRemaining}</span></div>
                  )}
                </div>
              </div>
            );
          })}

          {hasMoreToShow ? (
            <div
              ref={listLoadMoreSentinelRef}
              className="msg_list_load_more_sentinel"
              aria-hidden="true"
            />
          ) : null}

          {matrixLoadingMore ? (
            <div className="d-flex justify-content-center p-3">
              <Loader type="Oval" color="#F334BE" height={24} width={24} />
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
};

export default React.memo(NewMessagePage);
